import React from 'react';
import { Sparkles, CheckCircle2, XCircle, Pencil, AlertTriangle } from 'lucide-react';
import { cn } from '@hts/ui';
import { useGet, apiPost } from '../../hooks/useApi.js';

/**
 * Issue #62 — AI-proposed new ticket from an unmatched call.
 * Proposal card with Apply/Edit/Reject, reusing the PR #55 suggestion-card
 * look and feel (confidence + reason always; honest Unavailable + reason when
 * evidence is insufficient or a duplicate is likely).
 */

export interface NewTicketProposalDto {
  callId: string;
  available: boolean;
  confidence: number;
  reason: string;
  board: { boardId: number; name: string } | null;
  type: { typeId: number; name: string } | null;
  subtype: { subtypeId: number; name: string } | null;
  item: { itemId: number; name: string } | null;
  priority: { priorityId: number; name: string } | null;
  summary: string | null;
  requiredSkills: Array<{ skillId: string; name: string; source: string }>;
  duplicates: Array<{ cwTicketId: number; summary: string | null }>;
  computedAt: string;
}

export function NewTicketProposalCard({
  callId,
  onUpdate,
}: {
  callId: string;
  onUpdate?: () => void;
}) {
  const { data, loading, error, refetch } = useGet<NewTicketProposalDto>(
    `/api/v1/calls/${callId}/new-ticket`,
  );
  const [editing, setEditing] = React.useState(false);
  const [summary, setSummary] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [actionInfo, setActionInfo] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (data?.summary) setSummary(data.summary);
  }, [data?.computedAt]);

  async function run(fn: () => Promise<string>) {
    setBusy(true);
    setActionError(null);
    setActionInfo(null);
    try {
      setActionInfo(await fn());
      refetch();
      onUpdate?.();
    } catch (e) {
      setActionError(String((e as Error)?.message ?? e));
    } finally {
      setBusy(false);
    }
  }

  if (loading && !data) return null;
  if (error || !data) return null;

  // Defensive: tolerate partial DTOs (e.g. a proxy/gateway returning the call
  // object) without crashing the whole call detail page.
  const requiredSkills = Array.isArray(data.requiredSkills) ? data.requiredSkills : [];
  const duplicates = Array.isArray(data.duplicates) ? data.duplicates : [];

  const pct = Math.round((data.confidence ?? 0) * 100);

  return (
    <section
      aria-label="AI new-ticket proposal"
      data-testid="new-ticket-proposal"
      className={cn(
        'rounded-xl border p-4',
        data.available
          ? 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/10'
          : 'border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))]',
      )}
    >
      <div className="flex items-start gap-3">
        {data.available ? (
          <Sparkles className="h-4 w-4 text-blue-600 dark:text-blue-300 mt-0.5" />
        ) : (
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-300 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide">
              {data.available ? `New ticket proposal · ${pct}% confidence` : 'New ticket proposal'}
            </p>
            {!data.available && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-medium">
                Unavailable
              </span>
            )}
          </div>
          <p className="text-sm text-blue-800 dark:text-blue-200 mt-1" data-testid="new-ticket-reason">
            {data.reason}
          </p>

          {/* Part 1: the full proposed ticket (board, category, priority,
              skills, summary) is visible on the card even when unavailable —
              only the actions require availability. */}
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm sm:grid-cols-3">
                <div><dt className="text-xs text-[hsl(var(--content-subtle))]">Board</dt><dd className="text-[hsl(var(--content))] font-medium">{data.board?.name ?? '—'}</dd></div>
                <div><dt className="text-xs text-[hsl(var(--content-subtle))]">Category</dt><dd className="text-[hsl(var(--content))] font-medium">{[data.type?.name, data.subtype?.name, data.item?.name].filter(Boolean).join(' / ') || '—'}</dd></div>
                <div><dt className="text-xs text-[hsl(var(--content-subtle))]">Priority</dt><dd className="text-[hsl(var(--content))] font-medium">{data.priority?.name ?? '—'}</dd></div>
              </dl>
              {requiredSkills.length > 0 && (
                <p className="mt-2 text-xs text-[hsl(var(--content-subtle))]">
                  Required skills: {requiredSkills.map((s) => s.name).join(', ')}
                </p>
              )}
              {editing ? (
                <div className="mt-3 space-y-2">
                  <label htmlFor="new-ticket-summary" className="text-xs font-medium text-[hsl(var(--content-subtle))]">Summary</label>
                  <textarea
                    id="new-ticket-summary"
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    rows={2}
                    maxLength={200}
                    className="w-full text-sm text-[hsl(var(--content))] bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setEditing(false); }}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg border border-[hsl(var(--border))] text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))]"
                    >
                      Done editing
                    </button>
                  </div>
                </div>
              ) : (
                <p className="mt-2 text-sm text-[hsl(var(--content))] font-medium" data-testid="new-ticket-summary">
                  {summary ||
                    data.summary ||
                    'Proposed summary: Unavailable — no summary derived from the call.'}
                </p>
              )}
              {data.available && (
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <button
                  onClick={() => run(async () => {
                    const res = await apiPost<{ cwTicketId: number | null; mode: string }>(`/api/v1/calls/${callId}/new-ticket/apply`, { summary: summary || undefined });
                    return res.cwTicketId != null
                      ? `Ticket #${res.cwTicketId} created in ConnectWise.`
                      : 'Board is in shadow mode: the proposal decision was recorded for review (no ticket created).';
                  })}
                  disabled={busy}
                  className="flex items-center gap-1.5 rounded-lg bg-green-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {busy ? 'Creating…' : 'Apply & Create Ticket'}
                </button>
                <button
                  onClick={() => setEditing(true)}
                  disabled={busy}
                  className="flex items-center gap-1.5 rounded-lg border border-[hsl(var(--border))] px-3 py-1.5 text-xs font-medium text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] disabled:opacity-50"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button
                  onClick={() => run(async () => {
                    await apiPost(`/api/v1/calls/${callId}/new-ticket/reject`, {});
                    return 'Proposal rejected.';
                  })}
                  disabled={busy}
                  className="flex items-center gap-1.5 rounded-lg border border-[hsl(var(--border))] px-3 py-1.5 text-xs font-medium text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] disabled:opacity-50"
                >
                  <XCircle className="h-3.5 w-3.5" /> Reject
                </button>
              </div>
              )}

          {duplicates.length > 0 && (
            <ul className="mt-2 text-xs text-amber-700 dark:text-amber-300 list-disc list-inside">
              {duplicates.map((d) => (
                <li key={d.cwTicketId}>#{d.cwTicketId} {d.summary ?? ''}</li>
              ))}
            </ul>
          )}
          {actionInfo && <p className="mt-2 text-xs text-green-700 dark:text-green-300" data-testid="new-ticket-info">{actionInfo}</p>}
          {actionError && <p className="mt-2 text-xs text-red-600">{actionError}</p>}
        </div>
      </div>
    </section>
  );
}
